import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to DDoS yourself with analytics –– a war story",
  "description": "",
  "published": "2017-11-29T00:00:00.000Z",
  "redirect_from": ["/blog/ddos-yourself-analytics/swizec/7940"],
  "categories": "Front End, Startups, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`"Application error is preventing tutors from ending their shifts `}</em>{`😞"`}</p>
    <p>{`...`}</p>
    <p><em parentName="p">{`"Tutors in sessions are noticing that it says they are offline!"`}</em></p>
    <p>{`...`}</p>
    <p>{`It was 1:28am Monday night, two days before Thanksgiving. Literally the lowest hour of the lowest week for traffic all year. Our servers were melting.`}</p>
    <p>{`I saw the message by accident. Good thing I was up 😅`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/Screen-Shot-2017-11-28-at-23.44.45.png",
        "alt": null
      }}></img></p>
    <p>{`*`}{`"@sharon looks like everything is down and our servers are on fire. I restarted the server, can you see if that helped?"`}</p>
    <p>{`Average API response time → 30 seconds. Usually, it's 0.3 seconds.`}</p>
    <p>{`Restarting helped. Restarting a Rails server always helps. I went to bed.`}</p>
    <p>{`At 9am, our servers were melting.`}</p>
    <p>{`I restarted.`}</p>
    <p>{`At 10:30am, our servers were melting.`}</p>
    <p>{`We suspected analytics, deployed a quick patch to disable the workers, restarted servers, and got to work.`}</p>
    <p><img parentName="p" {...{
        "src": "/505be385d1c77f77493e23962920fbd7/media-l2QEgWxqxI2WJCXpC-giphy.gif",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "the-perfect-storm"
    }}>{`The perfect storm`}</h2>
    <blockquote>
      <p parentName="blockquote">{`Saving blobs of JSON into an array is hard`}</p>
      <p parentName="blockquote">{`~Swizec`}</p>
      <p parentName="blockquote">{`LoL it's just an array with some objects how hard can it be`}</p>
      <p parentName="blockquote">{`~The Internet`}</p>
    </blockquote>
    <p>{`So how does a server melt on the lowest week of traffic for the entire year? And how does it start melting again just a few hours later even though it was fine for many months before that?`}</p>
    <p>{`The problem with exponential problems is that you don't see them coming until it's too late.`}</p>
    <p>{`API response times grow millisecond by millisecond, getting ever so much larger. “Nothing alarming, normal fluctuation,” you say to yourself.`}</p>
    <p>{`Then BAM! Response times are 30 seconds and Heroku force timeouts all your requests. Now you're in trouble.`}</p>
    <p>{`That's why it happened so much faster the 2nd time. And even faster the 3rd time.`}</p>
    <p>{`But `}<em parentName="p">{`why`}</em>{`?`}</p>
    <p>{`Because saving a bunch of JSON blobs reliably is hard. The "reliably" part is what gets you.`}</p>
    <p>{`How do you make sure clients can send analytics events and ensure none of them get dropped?`}</p>
    <p>{`If the request fails, you store the event and try again later.`}</p>
    <p>{`How do you avoid sending too many requests since failing requests are the likeliest cause of most errors?`}</p>
    <p>{`You batch multiple events into the same request.`}</p>
    <p>{`Congratulations, you just created an API that can lead to your clients `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Denial-of-service_attack"
      }}>{`DDoSing`}</a>{` your own server.`}</p>
    <p>{`😁`}</p>
    <p>{`A DDoS is a distributed denial of service attack, by the way. It happens when so many clients send your server so many requests that it crumples under the pressure.`}</p>
    <p>{`Similar in principle to the Reddit or HackerNews `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Slashdot_effect"
      }}>{`hug of death`}</a>{`. That's when a link gets so popular that the flood of traffic melts servers.`}</p>
    <h2 {...{
      "id": "why-storing-retrying-and-batching-requests-spells-trouble"
    }}>{`Why storing, retrying, and batching requests spells trouble`}</h2>
    <p>{`You see, there is nothing wrong with storing failed requests and retrying them. Or with batching multiple small requests into one being one. It's the smart thing to do even.`}</p>
    <p>{`It's when your backend has bugs that it leads to trouble.`}</p>
    <p>{`What happens if you send a batch of 10 events and the server fails to process 1 of them? Say, because an error happens when attaching additional properties?`}</p>
    <p>{`The server says `}<em parentName="p">{`"Hey, I couldn't process this batch.”`}</em></p>
    <p>{`So you say, `}<em parentName="p">{`"Oh, it failed. I'll try again later.”`}</em></p>
    <p>{`Later comes. You have some new events, too. So you send a batch of 20 events.`}</p>
    <p>{`The same event fails. Next time, you send 30.`}</p>
    <p>{`See what that leads to yet?`}</p>
    <p>{`That's right. Eventually, you're sending so many events at the same time that the server has trouble processing them all within a single request. This begins to impact other clients' requests.`}</p>
    <p>{`The server is busy processing. Other clients say `}<em parentName="p">{`"Yo, here's my stuff”,`}</em>{` and get denied.`}</p>
    <p>{`Failures happen with no rhyme or reason. It depends completely on who is currently using the service and whether their client has a large backlog of events to send.`}</p>
    <p>{`The problem festers and grows.`}</p>
    <p>{`The more clients experience an issue, the more clients have large backlogs, the more issues occur.`}</p>
    <p><img parentName="p" {...{
        "src": "/0bed4c8211b4cb2b715566bba227e527/media-NQTttAGByybAI-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Eventually, your server melts, and nobody can't do nothing no more.`}</p>
    <h2 {...{
      "id": "the-fix"
    }}>{`The fix?`}</h2>
    <p>{`A preprocessing queue.`}</p>
    <p>{`When a client sends a request with some events, just store the raw payload somewhere and deal with it later.`}</p>
    <p>{`Requests almost always succeed, so events don't pile up on clients. Saving the raw payload is also fast, which makes overall performance faster for the clients, too.`}</p>
    <p>{`Processing offline also gives you more time. Instead of having to do everything in 30 seconds, you can spend many minutes on a big request if you have to.`}</p>
    <p>{`Plus, you can mark specific events as failing, but still process everything else.`}</p>
    <h2 {...{
      "id": "lessons-learned-→"
    }}>{`Lessons learned →`}</h2>
    <p>{`Saving arrays of JSON blobs is hard.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      